// Chat tab: thread of messages plus a composer at the bottom. The current
// `me` UserRef is supplied by the drawer (derived from the auth user); the
// composer attributes new messages to that identity.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEventHandler,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Icon, Textarea } from '../../../../ui/index.js';
import { roleLabel } from '../../../../shared/index.js';
import {
  useChatList,
  useMarkChatRead,
  useMarkChatUnread,
  usePostChat,
  useRequestChatUnreadSummary,
  useUnmarkChatUnread,
} from '../../queries.js';
import { formatRelative } from '../../format.js';
import type { ChatMessageView, RequestView, UserRef } from '../../types.js';

export interface ChatTabProps {
  request: RequestView;
  me: UserRef;
}

// WHY (#570): debounce window for the auto-read mutation. While a user
//   scrolls through many messages the IntersectionObserver fires for
//   each one — without a debounce we would emit one POST /chat/read
//   per message. 400ms covers a quick scroll-and-stop without
//   spamming the wire; the server-side `GREATEST(...)` upsert keeps
//   out-of-order arrivals safe.
const AUTO_READ_DEBOUNCE_MS = 400;

export function ChatTab({ request: r, me }: ChatTabProps) {
  const { t } = useTranslation('requests');
  const chatQuery = useChatList(r.id, { limit: 100 });
  const postChatMutation = usePostChat(r.id);
  const markRead = useMarkChatRead(r.id);
  const markUnread = useMarkChatUnread(r.id);
  const unmarkUnread = useUnmarkChatUnread(r.id);
  // INVARIANT (#570): the summary query is scoped to a single request id
  //   here so the mark/unmark button has authoritative `isMarkedUnread`
  //   without consulting the list-page hook (which is also valid but
  //   keyed on the loaded id-set). The realtime fan-out invalidates the
  //   summary prefix on any change, so this stays current.
  const unreadSummaryQuery = useRequestChatUnreadSummary([r.id]);
  const summary = unreadSummaryQuery.data?.[0];
  const isMarkedUnread = summary?.isMarkedUnread ?? false;

  const [text, setText] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const didInitialScrollRef = useRef(false);

  const messages = useMemo(() => {
    const wireItems = chatQuery.data?.items;
    if (!wireItems) return r.chat;
    return [...wireItems].reverse().map(
      (m): ChatMessageView => ({
        id: m.id,
        by: { id: m.by.id, name: m.by.name, role: m.by.role },
        at: Date.parse(m.at),
        text: m.text,
        // INVARIANT (#570 + exactOptionalPropertyTypes): conditional spread
        //   so `undefined` never lands in the projected value. A backend
        //   that omits the field (pre-#570 rollout, or future scope where
        //   counterparty cannot be derived) projects to "no key" — the
        //   `OwnCheckMark` renders single-check on the missing-key path.
        ...(m.readByCounterparty !== undefined ? { readByCounterparty: m.readByCounterparty } : {}),
      }),
    );
  }, [chatQuery.data?.items, r.chat]);

  // INVARIANT (#570): the open-time snapshot is captured ONCE per mount.
  //   It is the number of unread messages the user had when they opened
  //   the chat — the "New" divider is positioned against this snapshot
  //   and MUST NOT move while the user reads. Reopening (component
  //   remount) re-snapshots from the now-authoritative server state.
  const initialUnreadRef = useRef<number | null>(null);
  if (initialUnreadRef.current === null && summary !== undefined) {
    initialUnreadRef.current = summary.unreadCount;
  }
  const initialUnreadCount = initialUnreadRef.current ?? 0;

  // WHY (#570): "first unread" is derived from the snapshot — walk
  //   newest → oldest, count messages NOT authored by `me`, the
  //   message at which the count reaches `initialUnreadCount` is the
  //   oldest unread. Render the divider immediately ABOVE that message.
  //   Memoized off `messages.length` (not full array) because once the
  //   divider is placed it does not move on mutations.
  const firstUnreadMessageId = useMemo<string | null>(() => {
    if (initialUnreadCount <= 0) return null;
    let counter = 0;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i]!;
      if (m.by.id === me.id) continue;
      counter += 1;
      if (counter === initialUnreadCount) return m.id;
    }
    return null;
  }, [messages, initialUnreadCount, me.id]);

  // Initial scroll: jump to first-unread message if present, else to bottom.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (didInitialScrollRef.current) {
      // After the initial render, only auto-stick to bottom if the user
      // is already near the bottom (existing behavior preserved).
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distanceFromBottom <= 80) el.scrollTop = el.scrollHeight;
      return;
    }
    if (firstUnreadMessageId) {
      const target = el.querySelector<HTMLElement>(
        `[data-chat-message-id="${firstUnreadMessageId}"]`,
      );
      if (target) {
        // `block: 'start'` lines the first unread message up with the top
        // of the scroll viewport, so the "New" divider above it is
        // immediately visible.
        target.scrollIntoView({ block: 'start', behavior: 'auto' });
      } else {
        el.scrollTop = el.scrollHeight;
      }
    } else {
      el.scrollTop = el.scrollHeight;
    }
    didInitialScrollRef.current = true;
  }, [messages.length, firstUnreadMessageId]);

  // INVARIANT (#570): debounced auto-read. The IntersectionObserver
  //   tracks visible messages from OTHER users only; for each, we keep
  //   the chronologically newest visible id. A debounced flush sends
  //   that id to POST /chat/read. We gate at flush-time on
  //   `document.visibilityState === 'visible'` so a backgrounded tab
  //   (the observer may still fire on a layout-tied paint) does not
  //   advance the pointer.
  const pendingNewestRef = useRef<{ id: string; at: number } | null>(null);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushRead = useCallback(() => {
    flushTimerRef.current = null;
    const pending = pendingNewestRef.current;
    if (!pending) return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    pendingNewestRef.current = null;
    markRead.mutate({ messageId: pending.id });
  }, [markRead]);

  const scheduleRead = useCallback(
    (id: string, at: number) => {
      const prev = pendingNewestRef.current;
      if (!prev || at > prev.at) {
        pendingNewestRef.current = { id, at };
      }
      if (flushTimerRef.current !== null) {
        clearTimeout(flushTimerRef.current);
      }
      flushTimerRef.current = setTimeout(flushRead, AUTO_READ_DEBOUNCE_MS);
    },
    [flushRead],
  );

  useEffect(() => {
    return () => {
      if (flushTimerRef.current !== null) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
    };
  }, []);

  // Index for the observer callback: message id → timestamp (for the
  // newest-visible computation). Built off the current message list.
  const messageMeta = useMemo(() => {
    const m = new Map<string, { authorId: string; at: number }>();
    for (const msg of messages) m.set(msg.id, { authorId: msg.by.id, at: msg.at });
    return m;
  }, [messages]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        // Gate by tab visibility — `document.hidden` filters out the
        // backgrounded case where layout-tied entries fire on visibility
        // restore but the user has not actually returned focus.
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const id = (entry.target as HTMLElement).dataset.chatMessageId;
          if (!id) continue;
          const meta = messageMeta.get(id);
          if (!meta) continue;
          if (meta.authorId === me.id) continue; // own messages are not auto-read.
          scheduleRead(id, meta.at);
        }
      },
      { root: el, threshold: 0.5 },
    );
    const targets = el.querySelectorAll<HTMLElement>('[data-chat-message-id]');
    targets.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [messages.length, messageMeta, me.id, scheduleRead]);

  const send = () => {
    const trimmed = text.trim();
    if (!trimmed || postChatMutation.isPending) return;
    postChatMutation.mutate(trimmed, {
      onSuccess: () => setText(''),
    });
  };

  const onComposerKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    e.preventDefault();
    send();
  };

  const togglePending = markUnread.isPending || unmarkUnread.isPending;
  const onToggleMarkUnread = () => {
    if (togglePending) return;
    if (isMarkedUnread) {
      unmarkUnread.mutate();
    } else {
      markUnread.mutate();
    }
  };

  return (
    <div
      style={{
        padding: '12px 24px 0',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* Header row — mark/unmark toggle (#570). Compact: a single button
          near the composer rail. */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          paddingBottom: 8,
        }}
      >
        <button
          type="button"
          onClick={onToggleMarkUnread}
          disabled={togglePending}
          aria-pressed={isMarkedUnread}
          aria-label={isMarkedUnread ? t('chat.unmarkUnread') : t('chat.markUnread')}
          title={isMarkedUnread ? t('chat.unmarkUnread') : t('chat.markUnread')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 10px',
            borderRadius: 'var(--r-1)',
            border: '1px solid var(--border-1)',
            background: isMarkedUnread ? 'var(--status-error-bg)' : 'transparent',
            color: isMarkedUnread ? 'var(--status-error-fg)' : 'var(--fg-2)',
            fontSize: 'var(--fz-12)',
            cursor: togglePending ? 'wait' : 'pointer',
            opacity: togglePending ? 0.6 : 1,
          }}
        >
          <Icon name="circle-dot" size={13} />
          {isMarkedUnread ? t('chat.unmarkUnread') : t('chat.markUnread')}
        </button>
      </div>
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          paddingBottom: 12,
        }}
      >
        {!messages.length && (
          <div
            style={{
              padding: '48px 20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 10,
              color: 'var(--fg-3)',
            }}
          >
            <Icon name="message-square" size={24} style={{ color: 'var(--fg-4)' }} />
            <div style={{ fontSize: 13 }}>{t('chat.empty')}</div>
          </div>
        )}
        {messages.map((m) => {
          const isFirstUnread = firstUnreadMessageId === m.id;
          const isOwn = m.by.id === me.id;
          return (
            <div key={m.id}>
              {isFirstUnread && (
                <div
                  data-testid="chat-new-divider"
                  role="separator"
                  aria-label={t('chat.newDivider')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    margin: '6px 0',
                  }}
                >
                  <div style={{ flex: 1, height: 1, background: 'var(--status-error-fg)' }} />
                  <span
                    style={{
                      fontSize: 'var(--fz-11)',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      color: 'var(--status-error-fg)',
                    }}
                  >
                    {t('chat.newDivider')}
                  </span>
                  <div style={{ flex: 1, height: 1, background: 'var(--status-error-fg)' }} />
                </div>
              )}
              <div
                data-chat-message-id={m.id}
                style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}
              >
                <Avatar name={m.by.name} size={26} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--fg-1)' }}>
                      {m.by.name}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>
                      {roleLabel(m.by.role)}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>
                      · {formatRelative(m.at)}
                    </span>
                    {isOwn && <OwnCheckMark readByCounterparty={m.readByCounterparty === true} />}
                  </div>
                  <div
                    data-chat-message-text="true"
                    style={{
                      fontSize: 13,
                      color: 'var(--fg-1)',
                      marginTop: 3,
                      lineHeight: 1.5,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {m.text}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div
        style={{
          borderTop: '1px solid var(--border-2)',
          padding: '12px 0',
          position: 'relative',
        }}
      >
        <div style={{ position: 'relative' }}>
          <Textarea
            value={text}
            onChange={setText}
            onKeyDown={onComposerKeyDown}
            rows={2}
            placeholder={t('chat.placeholder', { name: me.name })}
            ariaLabel={t('chat.placeholder', { name: me.name })}
            style={{
              paddingRight: 46,
              resize: 'none',
            }}
          />
          <button
            type="button"
            aria-label={t('chat.send')}
            title={t('chat.send')}
            disabled={!text.trim() || postChatMutation.isPending}
            onClick={send}
            style={{
              position: 'absolute',
              right: 7,
              bottom: 7,
              width: 30,
              height: 30,
              borderRadius: 'var(--r-1)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background:
                text.trim() && !postChatMutation.isPending ? 'var(--accent-1)' : 'var(--bg-2)',
              color:
                text.trim() && !postChatMutation.isPending ? 'var(--fg-invert)' : 'var(--fg-3)',
              cursor: text.trim() && !postChatMutation.isPending ? 'pointer' : 'not-allowed',
              opacity: postChatMutation.isPending ? 0.6 : 1,
            }}
          >
            <Icon name="send-horizontal" size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

function OwnCheckMark({ readByCounterparty }: { readByCounterparty: boolean }): JSX.Element {
  const { t } = useTranslation('requests');
  const ariaLabel = readByCounterparty ? t('chat.aria.read') : t('chat.aria.sent');
  return (
    <span
      role="img"
      aria-label={ariaLabel}
      title={ariaLabel}
      data-testid={readByCounterparty ? 'chat-check-double' : 'chat-check-single'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        color: readByCounterparty ? 'var(--status-success-fg)' : 'var(--fg-3)',
      }}
    >
      <Icon name={readByCounterparty ? 'check-check' : 'check'} size={12} />
    </span>
  );
}
